import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, MapPin, Truck, Calendar, Home } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShippingPage() {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [isCartMode, setIsCartMode] = useState(false);
  const [pickupOnSite, setPickupOnSite] = useState(false);
  const [pickupEnabled, setPickupEnabled] = useState(false);
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    email: ""
  });

  useEffect(() => {
    const mode = sessionStorage.getItem("checkout_mode");
    if (mode === "cart") {
      const items = JSON.parse(sessionStorage.getItem("cart_items") || "[]");
      if (items.length === 0) { navigate("/"); return; }
      setCartItems(items);
      setIsCartMode(true);
    } else {
      const data = sessionStorage.getItem("order_data");
      if (!data) { navigate("/"); return; }
      setOrderData(JSON.parse(data));
    }
    fetchSettings();
    fetchActiveEvents();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setPickupEnabled(res.data.pickup_on_site_enabled || false);
    } catch (e) {
      
    }
  };

  const fetchActiveEvents = async () => {
    try {
      const res = await axios.get(`${API}/events/active`);
      setActiveEvents(res.data);
    } catch (e) {  }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    if (pickupOnSite) return form.email.trim() !== "";
    return form.name && form.address && form.city && form.state && form.zip && form.email;
  };

  const handleProceed = () => {
    const selectedEvent = activeEvents.find(e => e.id === selectedEventId);
    const shippingInfo = {
      shipping_name: pickupOnSite ? "" : form.name,
      shipping_address: pickupOnSite ? "" : form.address,
      shipping_city: pickupOnSite ? "" : form.city,
      shipping_state: pickupOnSite ? "" : form.state,
      shipping_zip: pickupOnSite ? "" : form.zip,
      shipping_email: form.email,
      pickup_on_site: pickupOnSite,
      pickup_event_id: pickupOnSite && selectedEvent ? selectedEvent.id : "",
      pickup_event_name: pickupOnSite && selectedEvent ? selectedEvent.name : "",
    };

    if (isCartMode) {
      sessionStorage.setItem("cart_shipping", JSON.stringify(shippingInfo));
      sessionStorage.setItem("checkout_mode", "cart");
    } else {
      const shippingData = { ...orderData, ...shippingInfo };
      sessionStorage.setItem("order_data", JSON.stringify(shippingData));
      sessionStorage.setItem("checkout_mode", "single");
    }
    navigate("/checkout");
  };

  const displayItem = isCartMode && cartItems.length > 0 ? cartItems[0] : orderData;
  if (!displayItem && !isCartMode) return null;

  return (
    <div className="min-h-screen" data-testid="shipping-page">
      {/* Nav */}
      <div className="border-b-2 border-black bg-white px-6 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(isCartMode ? "/cart" : `/customize/${orderData?.product_id}`)}
          className="flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider hover:text-[#FF3366] transition-colors"
          data-testid="back-to-customize-btn"
        >
          <ArrowLeft size={18} strokeWidth={2.5} /> Back
        </button>
        <span className="font-heading font-bold text-sm uppercase tracking-wider">Shipping & Delivery</span>
        <button onClick={() => navigate("/")} className="flex items-center gap-2 font-heading font-bold text-sm uppercase tracking-wider hover:text-[#FF3366] transition-colors" data-testid="continue-shopping-shipping">
          <Home size={18} strokeWidth={2.5} /> Shop
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-8 py-12">
        {/* Order Summary Mini */}
        <div className="brutal-card p-6 mb-8" data-testid="order-summary-mini">
          <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 block mb-3">Order Summary</span>
          {isCartMode ? (
            <div className="space-y-3">
              {cartItems.map((item, idx) => (
                <div key={item.id || idx} className="flex items-center justify-between">
                  <div>
                    <h3 className="font-heading font-bold text-sm uppercase">{item.product_name} x{item.quantity || 1}</h3>
                  </div>
                  <span className="font-heading font-black text-sm text-[#FF3366]">
                    ${((item.product_price + (item.front_engraving_total || 0) + (item.back_engraving_total || 0)) * (item.quantity || 1)).toFixed(2)}
                  </span>
                </div>
              ))}
              <div className="border-t border-zinc-200 pt-2 flex justify-between">
                <span className="font-heading font-bold text-xs uppercase">Total ({cartItems.length} items)</span>
                <span className="font-heading font-black text-lg text-[#FF3366]">
                  ${cartItems.reduce((s, i) => s + (i.product_price + (i.front_engraving_total || 0) + (i.back_engraving_total || 0)) * (i.quantity || 1), 0).toFixed(2)}
                </span>
              </div>
            </div>
          ) : orderData && (
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-sm uppercase">{orderData.product_name}</h3>
                <p className="text-xs text-zinc-500 mt-1">
                  {orderData.front_engravings?.map(e => e.name).join(', ') || 'Custom'} engraving
                </p>
              </div>
              <span className="font-heading font-black text-lg text-[#FF3366]">
                ${((orderData.product_price + (orderData.front_engraving_total || 0) + (orderData.back_engraving_total || 0)) * (orderData.quantity || 1)).toFixed(2)}
              </span>
            </div>
          )}
        </div>

        {/* Pickup On Site Toggle */}
        {pickupEnabled && (
          <div className="bg-[#00E5FF] border-2 border-black p-5 mb-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="pickup-toggle-section">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin size={20} strokeWidth={2.5} />
                <div>
                  <Label className="font-heading font-bold text-sm uppercase">Pick Up On Site</Label>
                  <p className="text-xs mt-0.5">We are at an event! Pick up your order here.</p>
                </div>
              </div>
              <Switch
                checked={pickupOnSite}
                onCheckedChange={setPickupOnSite}
                data-testid="pickup-switch"
              />
            </div>
            {pickupOnSite && activeEvents.length > 0 && (
              <div className="mt-4 pt-4 border-t border-black/20">
                <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2 flex items-center gap-1">
                  <Calendar size={12} strokeWidth={2.5} /> Select Event
                </label>
                <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                  <SelectTrigger className="bg-white border-2 border-black font-heading text-sm h-auto py-3" data-testid="event-select">
                    <SelectValue placeholder="Choose an event..." />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-black rounded-none">
                    {activeEvents.map(event => (
                      <SelectItem key={event.id} value={event.id}>
                        {event.name}{event.event_date ? ` — ${event.event_date}` : ""}{event.location ? ` (${event.location})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {/* Shipping Form */}
        {!pickupOnSite && (
          <div className="brutal-card p-8 mb-8 animate-slide-up" data-testid="shipping-form">
            <div className="flex items-center gap-3 mb-6">
              <Truck size={20} strokeWidth={2.5} />
              <h3 className="font-heading text-lg font-bold uppercase">Shipping Address</h3>
            </div>
            <div className="space-y-5">
              <div>
                <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Full Name</label>
                <input
                  className="brutal-input"
                  placeholder="John Doe"
                  value={form.name}
                  onChange={e => handleChange("name", e.target.value)}
                  data-testid="shipping-name-input"
                />
              </div>
              <div>
                <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Street Address</label>
                <input
                  className="brutal-input"
                  placeholder="123 Main St"
                  value={form.address}
                  onChange={e => handleChange("address", e.target.value)}
                  data-testid="shipping-address-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">City</label>
                  <input
                    className="brutal-input"
                    placeholder="Springfield"
                    value={form.city}
                    onChange={e => handleChange("city", e.target.value)}
                    data-testid="shipping-city-input"
                  />
                </div>
                <div>
                  <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">State</label>
                  <input
                    className="brutal-input"
                    placeholder="IL"
                    value={form.state}
                    onChange={e => handleChange("state", e.target.value)}
                    data-testid="shipping-state-input"
                  />
                </div>
              </div>
              <div>
                <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">ZIP Code</label>
                <input
                  className="brutal-input"
                  placeholder="62701"
                  value={form.zip}
                  onChange={e => handleChange("zip", e.target.value)}
                  data-testid="shipping-zip-input"
                />
              </div>
            </div>
          </div>
        )}

        {/* Email (always shown) */}
        <div className="brutal-card p-8 mb-8" data-testid="email-section">
          <label className="font-heading font-bold text-xs uppercase tracking-wider block mb-2">Email Address</label>
          <input
            type="email"
            className="brutal-input"
            placeholder="your@email.com"
            value={form.email}
            onChange={e => handleChange("email", e.target.value)}
            data-testid="shipping-email-input"
          />
          <p className="text-xs text-zinc-500 mt-2">We will send your order confirmation here.</p>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={() => navigate(isCartMode ? "/cart" : `/customize/${orderData?.product_id}`)}
            className="brutal-btn brutal-btn-outline text-sm"
            data-testid="back-btn"
          >
            <ArrowLeft size={14} className="inline mr-2" strokeWidth={2.5} />
            Back
          </button>
          <button
            onClick={handleProceed}
            disabled={!canProceed()}
            className={`brutal-btn text-sm flex items-center gap-2 ${!canProceed() ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="proceed-to-checkout-btn"
          >
            Continue to Payment
            <ArrowRight size={14} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
